import * as React from "react"
import { Link, usePage, router } from "@inertiajs/react"
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  useSidebar,
} from "@/components/ui/sidebar"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import { ChevronDown, ChevronRight, Users, Shield } from "lucide-react"
import type { NavItem } from "@/config/navigation"

interface User {
  id: number
  name?: string
  username?: string
  email: string
  role: 'user' | 'admin' | 'manager'
  role_level?: number
}

interface AppSidebarProps extends React.ComponentProps<typeof Sidebar> {
  // For backward compatibility - single list of items
  navigationItems?: NavItem[]
  // New grouped navigation props
  flatItems?: NavItem[]
  managerItems?: NavItem[]
  adminItems?: NavItem[]
  user: User
}

// Cookie constants for menu state persistence
const MANAGER_MENU_COOKIE_NAME = "manager_menu_state"
const ADMIN_MENU_COOKIE_NAME = "admin_menu_state"
const MENU_COOKIE_MAX_AGE = 60 * 60 * 24 * 7 // 7 days

// Helper function to get cookie value
function getCookieValue(name: string): boolean {
  if (typeof document === 'undefined') return false
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'))
  return match ? match[2] === 'true' : false
}

// Helper function to set cookie value
function setCookieValue(name: string, value: boolean) {
  if (typeof document === 'undefined') return
  document.cookie = `${name}=${value}; path=/; max-age=${MENU_COOKIE_MAX_AGE}`
}

export function AppSidebar({ 
  navigationItems, 
  flatItems = [], 
  managerItems = [], 
  adminItems = [], 
  user, 
  ...props 
}: AppSidebarProps) {
  const { url } = usePage()
  const { setOpenMobile } = useSidebar()
  
  // State for collapsible groups - read from cookies, default to false
  const [isManagerOpen, setIsManagerOpen] = React.useState(() => getCookieValue(MANAGER_MENU_COOKIE_NAME))
  const [isAdminOpen, setIsAdminOpen] = React.useState(() => getCookieValue(ADMIN_MENU_COOKIE_NAME))

  // If old format is passed, convert to new format
  const displayFlatItems = navigationItems 
    ? navigationItems.map(item => ({ ...item, id: item.href, group: 'user' }))
    : flatItems

  // Handle navigation link click to close mobile menu
  const handleNavLinkClick = (href: string, event: React.MouseEvent) => {
    event.preventDefault()
    
    if (setOpenMobile) {
      setOpenMobile(false)
    }
    
    setTimeout(() => {
      router.visit(href, {
        preserveState: false,
        onFinish: () => {
          if (setOpenMobile) {
            setOpenMobile(false)
          }
        },
        onError: () => {
          if (setOpenMobile) {
            setOpenMobile(true)
          }
        }
      })
    }, 100)
  }

  // Render a single navigation item
  const renderNavItem = (item: NavItem) => {
    const Icon = item.icon
    return (
      <SidebarMenuItem key={item.id || item.href}>
        <SidebarMenuButton asChild isActive={url === item.href}>
          <Link 
            href={item.href} 
            onClick={(event) => handleNavLinkClick(item.href, event)}
            className="flex items-center gap-2"
          >
            {Icon && <Icon />}
            <span>{item.label}</span>
          </Link>
        </SidebarMenuButton>
      </SidebarMenuItem>
    )
  }

  // Render collapsible group with cookie persistence
  const renderCollapsibleGroup = (
    isOpen: boolean,
    setIsOpen: (v: boolean) => void,
    cookieName: string,
    title: string,
    icon: React.ComponentType,
    items: NavItem[]
  ) => {
    if (items.length === 0) return null
    
    const Icon = icon
    
    // Handle open change and persist to cookie
    const handleOpenChange = (open: boolean) => {
      setCookieValue(cookieName, open)
      setIsOpen(open)
    }
    
    return (
      <Collapsible
        key={title}
        open={isOpen}
        onOpenChange={handleOpenChange}
        className="group/collapsible"
      >
        <SidebarGroup>
          <CollapsibleTrigger asChild>
            <SidebarGroupLabel className="flex items-center justify-between px-2 py-1.5 text-sm font-semibold hover:bg-accent hover:text-accent-foreground rounded-md cursor-pointer">
              <div className="flex items-center gap-2">
                <Icon />
                <span>{title}</span>
              </div>
              {isOpen ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </SidebarGroupLabel>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <SidebarGroupContent>
              <SidebarMenu>
                {items.map(renderNavItem)}
              </SidebarMenu>
            </SidebarGroupContent>
          </CollapsibleContent>
        </SidebarGroup>
      </Collapsible>
    )
  }

  return (
    <Sidebar {...props}>
      <SidebarHeader>
        <Link href="/blog" className="flex items-center gap-2 px-2 py-2">
          <img src="/images/nav-logo-32.webp" alt="MantraVid" className="h-8 w-8" />
        </Link>
      </SidebarHeader>
      <SidebarContent>
        {/* User-level items - shown flat without group */}
        {displayFlatItems.length > 0 && (
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu>
                {displayFlatItems.map(renderNavItem)}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        {/* Manager-level items - collapsible group */}
        {renderCollapsibleGroup(
          isManagerOpen, 
          setIsManagerOpen,
          MANAGER_MENU_COOKIE_NAME, 
          'Manager', 
          Users, 
          managerItems
        )}

        {/* Admin-level items - collapsible group */}
        {renderCollapsibleGroup(
          isAdminOpen, 
          setIsAdminOpen,
          ADMIN_MENU_COOKIE_NAME, 
          'Admin', 
          Shield, 
          adminItems
        )}
      </SidebarContent>
      <SidebarRail />
    </Sidebar>
  )
}
